/**
 * Die Revisionsansicht: Dateiliste, Diff, Umschalter auf die vollständige Datei,
 * Zeilenmarkierung und der Befundverlauf. Das ist die Interaktion, um die herum
 * die ganze Oberfläche gebaut ist (Spec, „Revisionsansicht").
 *
 * Fünf Festlegungen tragen sie:
 *
 * - **Der Diff kommt als strukturierte Hunks und wird hier gerendert**
 *   (ADR-0008). Serverseitig gerendertes HTML nähme dieser Ansicht die
 *   Zeilenmarkierung und den Anker, an dem ein Befund hängt.
 * - **Die vollständige Datei ist kein Beiwerk.** Sie zeigt den Zusammenhang, in
 *   dem das Geänderte steht — auch die Zeilen, die die Revision nicht angefasst
 *   hat. Markierbar ist dort dasselbe wie im Diff, und der Umschalter verliert
 *   die Markierung nicht.
 * - **Markierbar ist nur, was diese Revision geändert hat** (ADR-0019): die
 *   hinzugefügten Zeilen und die Zeile unmittelbar vor einer Löschung, bei einer
 *   hinzugefügten Datei die ganze. Was die Ansicht nicht annimmt, begründet sie
 *   an der Markierungszeile — ein Befund anderswo stünde in keiner Revision.
 * - **Der Anker ist Projekt, Pfad, Revision, Zeilenbereich** (ADR-0006), und die
 *   Revision ist immer die begutachtete. Welche Zeilen er nennen darf, rechnet
 *   `anchor.ts`; hier wird es angezeigt. Der Anwender rechnet nicht.
 * - **Die Markierung liegt hier und nicht in der geöffneten Datei.** Der
 *   Befundverlauf springt an den Anker eines abgelegten Befundes zurück, und der
 *   führt in aller Regel in eine *andere* Datei — läge die Markierung eine Ebene
 *   tiefer, wäre sie beim Wechsel schon wieder fort.
 * - **Eine Revision ist auch dann ansehbar, wenn ihre Begutachtung noch läuft.**
 *   Der Bearbeitungsstand steht im Kopf; gewartet wird auf nichts.
 *
 * **Diese Ansicht sitzt nicht auf einer Karte.** Sie ist der Inhalt des
 * Fensters und keine Kachel darin; der Kasten um sie herum kostete auf jeder
 * Seite Breite, die dem Code und dem Befundtext fehlt. Sie steht deshalb in
 * denselben vollflächigen Abschnitten wie die Listen — Überschrift, eine
 * Trennlinie, Inhalt, nirgends ein Rahmen. Das Design-System sagt dasselbe:
 * „Record sections are full-bleed …, divided by a single top rule, never boxed."
 *
 * **Die Reihenfolge ist Titel, Navigationszeile, Commit-Kommentar, Dateien,
 * Befunde.** Der Kommentar steht vor den Dateien, weil er sagt, was beabsichtigt
 * war, und die Dateien sagen, was geschehen ist — er ist Material der Revision
 * und kein Beiwerk der Navigationsleiste, in der er einzeilig geklemmt stand.
 * Die Dateien
 * stehen wieder oben und **aufgeklappt**: Es gibt genau **eine** Codefläche in
 * dieser Ansicht, und die ist immer da. Vorher klappte der Code an jeder Stelle
 * des Befundverlaufs einzeln auf, und damit hing an der Frage „welche Fläche ist
 * offen" die Markierung, aus der ein Befund entsteht. Der Weg aus dem
 * Befundverlauf in den Code ist derselbe geblieben — er führt nun nach oben,
 * markiert die Zeilen und holt sie ins Bild.
 *
 * **Von hier führt die Übergabe weiter** (#100): ein Knopf in der Aktionsleiste,
 * der den kopierfertigen Text zu **allen** Stellen dieser Revision holt, ihn in
 * die Zwischenablage legt und ihn zur Vorschau zeigt. Es ist derselbe Dialog wie
 * am Knopf einer Stelle im Befundvorrat und nicht ein zweiter daneben — was sich
 * unterscheidet, ist allein der Zuschnitt des Textes. Zusammengestellt wird er im
 * Server; die Ansicht setzt keinen Text zusammen, den sie nur zur Hälfte hat.
 *
 * **Von hier führt der Weg zur vorhergehenden und zur nächsten Revision.** Wer
 * eine Reihe durchgeht, geht sie durch und kehrt nicht zwischen je zwei
 * Revisionen in die Liste zurück. Welche die Nachbarn sind, sagt der Server
 * (`previous`, `next`) — nach Nummer und im selben Projekt.
 *
 * **Und daneben führt ein Knopf dorthin, wo für mich noch etwas offen ist**
 * (#185): in die nächste Revision, in der eine **unbewertete Stelle an meinem
 * Code** liegt. Welche das ist, sagt der **Befundvorrat** — mit fest diesen zwei
 * Bedingungen und in seiner Ordnung; eine zweite Reihenfolge wird dafür nicht
 * erfunden, und der Filter der Übersicht geht ihn nichts an. Gibt es keine
 * solche Revision, sagt er das und bewegt nichts.
 *
 * Ganz unten steht die **Gegenüberstellung**, und zwar **eingeklappt**. Sie ist
 * vor der Abgabe leer und sagt das; unmittelbar danach stehen die Befunde der
 * Agenten neben den eigenen. Dass sie eine Abgabe braucht, ist richtig — dass
 * sie deshalb den Platz über dem Befundverlauf beanspruchte, war es nicht: Sie
 * ist ein Nebenprodukt für den, der es sucht, und keine Vorbedingung dafür,
 * Befunde zu sehen. Was sie zeigt, entscheidet der Server (ADR-0007).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { Button, Chip, Icon, IconButton, IconTile, StatusPill, TabStrip } from "../../design-system";
import type {
  ChangeAction,
  DiffHunk,
  DiffLine,
  FileChangeDetail,
  FileChangeSummary,
  Finding,
  MessagePart,
  Revision,
  RevisionDetail,
  RevisionNeighbour,
  RevisionViewApi,
  Role,
  SiteQuery,
} from "../../api/client";
import { formatNumber, formatTimestamp } from "../../formatting";
import type { Tone } from "../findings";
import { HandoffButton, HandoffDialog, REVISION_HANDOFF_HINT, useHandover } from "../handoff";
import { Toolbar } from "../../shell/Toolbar";
import { ListPage, ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";
import { bringIntoView, bringWithin } from "../scrolling";
import { StateBadge } from "../RevisionStreamView";
import { ComparisonPanel } from "./ComparisonPanel";
import { FindingLog } from "./FindingLog";
import { CodeLine, CodeLines, labelOfDiffLine } from "./code";
import { highlight, languageOf, tintDiff } from "./highlight";
import type { Language, Token } from "./highlight";
import {
  anchorOf,
  contentLines,
  covers,
  describeAnchor,
  extendMarking,
  findableLines,
  markLine,
  markingAt,
  markingOfFinding,
  reasonAgainst,
  sideOfDiffLine,
  sideOfWholeFile,
} from "./anchor";
import type {
  AnchoredFile,
  FindableLines,
  FindingAnchor,
  LineSide,
  Marking,
} from "./anchor";

/** Die beiden Darstellungen einer Dateiänderung. */
type Presentation = "diff" | "file";

const PRESENTATION_LABELS: Record<Presentation, string> = {
  diff: "Diff",
  file: "Vollständige Datei",
};

/**
 * Die Reiter der beiden Darstellungen, in ihrer Reihenfolge — und der Weg von
 * einer Beschriftung zurück auf ihre Kennung.
 *
 * `TabStrip` wählt über die **Beschriftung**: Der Baustein des Skills kennt keine
 * Kennung neben dem Text. Der Zustand bleibt trotzdem die englische Kennung, denn
 * sie übersteht eine Umformulierung der Beschriftung (ADR-0009).
 */
const PRESENTATIONS: Presentation[] = ["diff", "file"];
const PRESENTATION_TABS = PRESENTATIONS.map((one) => PRESENTATION_LABELS[one]);

function presentationOf(label: string): Presentation {
  return PRESENTATIONS.find((one) => PRESENTATION_LABELS[one] === label) ?? "diff";
}

/** Die Beschriftungen der Art einer Änderung — die Werte selbst sind englisch. */
const ACTION_LABELS: Record<ChangeAction, string> = {
  added: "hinzugefügt",
  modified: "geändert",
  deleted: "gelöscht",
  moved: "verschoben",
};

/**
 * Jede der vier Änderungsarten ist ein Zustand und trägt eine Farbe — hier gibt
 * es keinen farblosen Fall, und deshalb auch keinen `Chip` (#141).
 */
const ACTION_TONES: Record<ChangeAction, Tone> = {
  added: "green",
  modified: "blue",
  deleted: "red",
  moved: "amber",
};

const NOTHING_MARKED = "Keine Zeile markiert. Eine Zeile anklicken, mit Umschalt einen Bereich.";

const MIXED_SIDES =
  "Eine Markierung umfasst nie entfernte und erhaltene Zeilen zugleich — " +
  "ein Befund nennt genau eine Revision.";

/** Was am Rand des Bestands dasteht, statt eines Weges, der ins Leere führte. */
const NO_PREVIOUS = "Die erste Revision dieses Projekts — davor liegt nichts.";

const NO_NEXT = "Die neueste Revision dieses Projekts — dahinter liegt nichts.";

/**
 * Der **Knopf**, der aus einer durchgearbeiteten Revision heraus weiterführt
 * (#185) — er bewegt und bewertet nicht, und ist deshalb keiner der vier
 * Handgriffe (`CONTEXT.md`, ADR-0022).
 *
 * Der Glossarbegriff steht wörtlich darin: **„an meinem Code"** sind die
 * Befunde, deren Ankerrevision die fragende Person übergeben hat (ADR-0028).
 */
const NEXT_OWN_CODE = "Nächste Revision mit offenem Befund an meinem Code";

/** Was der Knopf sucht — und was er ausdrücklich nicht tut. */
const NEXT_OWN_CODE_TITLE =
  "Die nächste Revision des Befundvorrats, in der eine unbewertete Stelle an Ihrem Code "
  + "liegt — nicht die numerisch nächste. Bewertetes und Überschriebenes wird übersprungen, "
  + "und was in dieser Revision noch offen ist, hält ihn nicht auf.";

/**
 * Was dasteht, wenn es keine solche Revision gibt.
 *
 * Gesagt wird es, und der Knopf bleibt stehen: Ein Knopf, der nichts tut und
 * nichts sagt, liest sich wie ein Fehler.
 */
const NO_OWN_CODE_LEFT =
  "Im Befundvorrat steht keine weitere Revision, in der eine unbewertete Stelle an "
  + "Ihrem Code liegt.";

/**
 * Die Frage, die der Knopf an den **Befundvorrat** stellt: **unbewertet** und
 * **an meinem Code**, sonst nichts.
 *
 * Fest diese zwei Bedingungen und nicht der Filter, den jemand auf der Übersicht
 * eingestellt hat — die Revisionsansicht bekommt ihn gar nicht, und das bleibt
 * so. Der Sprung trägt seinen Filter im Namen.
 *
 * **Die Seitengröße ist die des Vorrats und keine größere.** Jede ausgelieferte
 * Stelle wird vermerkt und verbraucht die Blindabsicht für ihre Revision
 * (ADR-0013); wer hier eine ganze Seite mehr holte, verbrauchte sie für
 * Revisionen, die niemand ansehen wollte. Reicht die Seite nicht, wird geblättert.
 */
const OWN_OPEN_SITES: Omit<SiteQuery, "page"> = {
  perPage: 25,
  projectId: null,
  path: null,
  kind: null,
  severity: null,
  status: null,
  qualification: "unjudged",
  origin: null,
  from: null,
  to: null,
  thresholds: false,
  committer: "me",
};

/**
 * Wie eine Zeile angeklickt wird — und was daraus wird, entscheidet die Ansicht.
 *
 * `findable` reicht die geöffnete Dateiänderung nach oben: Welche Zeilen ein
 * Befund nennen darf, steht im Diff, und der ist erst dort geladen. Entschieden
 * wird trotzdem oben, wo die Markierung liegt — zwei Stellen, die dieselbe Frage
 * beantworten, beantworten sie irgendwann verschieden.
 */
type Picking = (side: LineSide, line: number, extend: boolean, findable: FindableLines) => void;

export function RevisionView({
  client,
  revisionId,
  role,
  target = null,
  back = "Zurück zu den Revisionen",
  onClose,
  onOpenRevision,
  onTitle,
}: {
  client: RevisionViewApi;
  revisionId: number;
  /** Sie entscheidet allein über die Anzeige des Nachvollzugs, nicht über den Zugriff. */
  role: Role;
  /**
   * Die Stelle, an die diese Ansicht springen soll — der Weg aus dem
   * **Befundvorrat** (Spec v1.3, Story 220).
   *
   * Sie wird einmal angewandt, sobald die Dateiliste steht, und danach nicht
   * wieder: Wer anschließend selbst markiert, soll die Markierung behalten. Der
   * Anker genügt zum Zurückfinden — die Datei wird über den **Pfad** gesucht
   * (ADR-0006).
   */
  target?: FindingAnchor | null;
  /**
   * Wohin der Rückweg führt, ausgeschrieben — der Titel des Pfeils oben links.
   *
   * Er steht hier als Text und nicht als Bereich: Diese Ansicht kennt die
   * Bereiche nicht, sie wird aus zweien geöffnet, und „zurück zu den
   * Revisionen" wäre aus den Befunden heraus eine Ansage, die nicht stimmt.
   */
  back?: string;
  onClose: () => void;
  /**
   * Der Weg zur vorhergehenden oder zur nächsten Revision.
   *
   * Er wird nach oben gemeldet und nicht hier gegangen: Wo diese Ansicht steht,
   * führt die Hülle — in der Chronik des Browsers, damit Zurück dorthin führt,
   * wo man herkam. Fehlt er, stehen die beiden Schalter gar nicht erst da.
   */
  onOpenRevision?: (id: number) => void;
  /**
   * Wie diese Revision heißt — für den zweiten Eintrag der **Brotkrume** (#41).
   *
   * Er wird nach oben gemeldet und nicht dort gerechnet: Die Adresse einer
   * geöffneten Revision trägt ihre Kennung (`/revisions/3`) und nicht ihre
   * SVN-Nummer, und die Nummer steht erst in der geladenen Antwort. `null`
   * solange sie fehlt — dann steht in der Brotkrume „Revision" und keine
   * erfundene Zahl.
   */
  onTitle?: (label: string | null) => void;
}): ReactElement {
  const { state } = useLoaded(() => client.revision(revisionId));
  const [openChangeId, setOpenChangeId] = useState<number | null>(null);
  const [presentation, setPresentation] = useState<Presentation>("diff");
  const [marking, setMarking] = useState<Marking | null>(null);
  // Warum der letzte Klick keine Markierung ergeben hat — der Satz steht an der
  // Markierungszeile. `null` heißt: Es gibt nichts zu begründen.
  const [refusal, setRefusal] = useState<string | null>(null);
  // Nach der Abgabe lädt die Gegenüberstellung neu. Der Zähler ist ihr
  // Schlüssel: Sie hält ihren Ladezustand selbst, und ein neuer Schlüssel ist
  // der kürzeste Weg, ihn zu verwerfen.
  const [submissions, setSubmissions] = useState(0);
  // Das Formular für einen neuen Befund. Der Schalter liegt hier, weil ihn zwei
  // Stellen umlegen: der Knopf im Kopf des Befundverlaufs und der an der
  // Markierungszeile am Code. Der Anwender markiert im Code und soll dort
  // weitermachen können, ohne zu scrollen.
  const [composing, setComposing] = useState(false);
  // Wie oft aus dem Befundverlauf in den Code gesprungen wurde. Nicht *ob*,
  // sondern *wie oft*: Zweimal auf dieselbe Stelle zu springen ist zweimal
  // derselbe Wunsch, sie zu sehen — und muss die Fläche zweimal heranholen.
  const [jumps, setJumps] = useState(0);
  // Was der Knopf „nächste Revision … an meinem Code" zu sagen hatte (#185):
  // dass es keine gibt, oder warum der Vorrat nicht antwortet. `null` heißt, es
  // gibt nichts zu sagen — gesprungen wird, statt geredet.
  const [detour, setDetour] = useState<string | null>(null);

  // Die **Übergabe** zu dieser Revision: derselbe Dialog wie an einer Stelle im
  // Vorrat, nur mit dem Text eine Ebene höher (#100).
  const handover = useHandover();

  const detail = state.kind === "loaded" ? state.value : null;
  const changes = detail?.fileChanges ?? [];

  // Ohne eigene Wahl steht die erste Dateiänderung offen: Eine Ansicht, die
  // nichts zeigt, bis jemand klickt, verschenkt den ersten Blick.
  const chosen = changes.find((one) => one.id === openChangeId) ?? changes[0] ?? null;
  const file = detail === null || chosen === null ? null : anchoredFile(detail.revision, chosen);
  const anchor = file === null || marking === null ? null : anchorOf(file, marking);

  // Der Sprung aus dem Befundvorrat: Er kann erst greifen, wenn die Dateiliste
  // steht — die Stelle wird über ihren Pfad gesucht, und den kennt erst sie.
  // Genau einmal, denn danach gehört die Markierung dem Anwender.
  const jumped = useRef(false);

  // Ob die Markierung aus einem Sprung stammt. Nur dann scrollt der Code von
  // selbst: Wer eine Zeile anklickt, sieht sie schon — eine Ansicht, die
  // daraufhin scrollte, nähme ihm die Stelle unter dem Zeiger weg.
  const [arrived, setArrived] = useState(false);

  // Die Fläche mit den Dateien — das Ziel eines Sprungs aus dem Befundverlauf.
  const files = useRef<HTMLDivElement | null>(null);

  // **Der Name dieses Ortes, nach oben gemeldet** — der zweite Eintrag der
  // Brotkrume (#41). Dieselbe Schreibweise wie in jeder Zeile des Stroms und im
  // Befundvorrat: `r4.711`, damit die Zahl von sich aus sagt, dass sie eine
  // Revision ist.
  useEffect(() => {
    onTitle?.(detail === null ? null : `r${formatNumber(detail.revision.number)}`);
  }, [detail, onTitle]);

  useEffect(() => {
    if (jumped.current || target === null || detail === null) {
      return;
    }

    jumped.current = true;

    const found = detail.fileChanges.find((one) => one.path === target.path);

    if (found === undefined) {
      return;
    }

    setOpenChangeId(found.id);
    setMarking(markingAt(target));
    setArrived(true);

    // Die Seite bleibt dabei, wo der Befundverlauf sie hinstellt: Wer aus dem
    // Vorrat kommt, hat auf einen **Befund** geklickt und will ihn sehen. Der
    // Code steht markiert oben und wartet.
  }, [detail, target]);

  // Der Sprung aus dem Befundverlauf holt die Codefläche ins Bild. Er steht hier
  // und nicht im Verlauf: Dort weiß niemand, wo der Code steht.
  useEffect(() => {
    if (jumps > 0) {
      bringIntoView(files.current, "start");
    }
  }, [jumps]);

  /**
   * Eine andere Datei öffnen. Die Markierung fällt dabei weg: Ein Anker aus einer
   * anderen Datei wäre nach dem Wechsel schlicht falsch.
   */
  const choose = (id: number): void => {
    setOpenChangeId(id);
    setMarking(null);
    setRefusal(null);
    setArrived(false);
  };

  /**
   * Eine Zeile anklicken: ohne Umschalt eine neue Markierung, mit Umschalt der
   * Bereich von der zuerst geklickten Zeile bis hierher.
   *
   * Zweimal wird abgelehnt, und beide Male bleibt die bestehende Markierung
   * stehen und die Ansicht nennt den Grund: über die Seiten hinweg wird nicht
   * erweitert, und **eine Zeile, die diese Revision nicht geändert hat, trägt
   * keinen Befund** (ADR-0019). Das Zweite steht hier und nicht am Code: Geprüft
   * wird der Bereich, der entstünde, und nicht die angeklickte Zeile — ein
   * Bereich, der eine geänderte Zeile berührt, ist erlaubt (ADR-0016).
   */
  const pick: Picking = (side, line, extend, findable) => {
    setArrived(false);

    const next =
      extend && marking !== null ? extendMarking(marking, side, line) : markLine(side, line);

    if (next === null) {
      setRefusal(MIXED_SIDES);

      return;
    }

    const reason = reasonAgainst(findable, next.side, next.startLine, next.endLine);

    if (reason !== null) {
      setRefusal(reason);

      return;
    }

    setMarking(next);
    setRefusal(null);
  };

  /**
   * Der Sprung aus dem Befundverlauf: Er findet die Datei über den **Pfad** des
   * Ankers und nicht über eine mitgeführte Kennung — der Anker ist Datei @
   * Revision, und das genügt (ADR-0006).
   *
   * Er öffnet die Datei in der einen Codefläche oben, markiert die Zeilen und
   * holt die Fläche heran. Vorher klappte an jeder Stelle eine eigene Fläche
   * auf; zwei davon hätten zwei Markierungen gehabt, und ein Befund wäre an der
   * entstanden, die der Anwender gerade nicht ansieht.
   */
  const jump = (found: Finding): void => {
    const target = changes.find((one) => one.path === found.path);

    if (target === undefined || detail === null) {
      return;
    }

    setOpenChangeId(target.id);
    setMarking(markingOfFinding(found));
    setRefusal(null);
    setArrived(true);
    setJumps((previous) => previous + 1);
  };

  return (
    <>
      {/*
        **Die Werkzeugleiste dieser Ansicht** (#41). Sie trägt die drei Knöpfe,
        die vorher als eigene Zeile im Inhalt standen: den Rückweg, die Übergabe
        und den Schrittschalter. Alle drei handeln von der Revision als Ganzem
        oder führen aus ihr heraus — und keiner von ihnen gehört in eine Sektion.
        Rechts bleibt sie leer: Hier gibt es nichts zu blättern.
      */}
      <Toolbar>
        <IconButton
          icon="arrow-left"
          tip={back}
          size="sm"
          tone="quiet"
          onClick={onClose}
        />

        {detail === null ? null : (
          <>
            {/* Die **Übergabe** steht vor dem Schrittschalter: Sie handelt von
                dieser Revision, der Schalter führt aus ihr heraus. */}
            <HandoffButton
              onHandOver={() =>
                handover.begin(REVISION_HANDOFF_HINT, () => client.revisionHandoff(revisionId), {
                  // Der Zuschnitt „ganze Revision": kein Anker, weil der Text
                  // alles trägt, was in ihr liegt (#100, #102).
                  revisionId,
                  anchor: null,
                })
              }
            />

            {onOpenRevision === undefined ? null : (
              <>
                <RevisionStepper detail={detail} onOpenRevision={onOpenRevision} />

                {/* **Daneben und nicht darin**: Der Schrittschalter führt zum
                    unmittelbaren Nachbarn, ohne jeden Filter. Dieser hier heißt,
                    was er tut — ein Weiterblättern, das stillschweigend
                    überspringt, wäre etwas anderes. */}
                <NextOwnCodeButton
                  client={client}
                  revisionId={revisionId}
                  onOpenRevision={onOpenRevision}
                  onSay={setDetour}
                />
              </>
            )}
          </>
        )}
      </Toolbar>

      <ListPage>
        <RevisionHeader detail={detail} />

        <div style={{ padding: "0 var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {detail !== null && changes.length === 0 ? (
            <Notice text="Diese Revision hat keine Dateiänderung." />
          ) : null}

          {/* Was der Sprung an meinem Code zu sagen hatte (#185) — hier und
              nicht in der Werkzeugleiste: Sie trägt Knöpfe, keine Sätze. */}
          {detour === null ? null : <Notice text={detour} />}
        </div>

        {/* **Der Commit-Kommentar steht vor den Dateien.** Er sagt, was
            beabsichtigt war, die Dateien sagen, was geschehen ist — und in dieser
            Reihenfolge werden sie gelesen. */}
        {detail === null ? null : (
          <CommitMessage message={detail.revision.message} parts={detail.messageParts} />
        )}

        {/* **Die Dateien stehen oben und offen.** Sie sind der Umfang der
            Revision, und sie tragen die eine Codefläche dieser Ansicht — die, in
            der markiert wird und aus der ein Befund entsteht. Einklappen lässt sie
            sich nicht: Eine Ansicht, in der die Markierung hinter einem Knopf
            verschwindet, hat zwei Zustände, in denen dieselbe Geste etwas
            anderes tut. */}
        {chosen === null ? null : (
          <div ref={files}>
            <ListSection
              title="Dateien dieser Revision"
              glyph="folder-tree"
              headerRight={<Chip>{formatNumber(changes.length)}</Chip>}
            >
              <div
                style={{
                  display: "flex",
                  minHeight: 420,
                  borderTop: "1px solid var(--line-f)",
                }}
              >
                <FileList changes={changes} chosenId={chosen.id} onChoose={choose} />

                <FilePanel
                  key={chosen.id}
                  client={client}
                  change={chosen}
                  presentation={presentation}
                  onPresentation={setPresentation}
                  anchor={anchor}
                  marking={marking}
                  arrived={arrived}
                  refusal={refusal}
                  onPick={pick}
                  onCompose={() => setComposing(true)}
                />
              </div>
            </ListSection>
          </div>
        )}

        {detail === null ? null : (
          <FindingLog
            client={client}
            revisionId={revisionId}
            // Die Rolle entscheidet über den Knopf „Muster daraus machen"
            // am Befund — sichtbar allein für `admin` (ADR-0033).
            role={role}
            anchor={anchor}
            // Der Sprung aus dem Befundvorrat führt nicht an die Datei, sondern
            // an die Stelle: Der Verlauf hebt sie hervor und scrollt sie heran.
            target={target}
            fileChangeId={chosen?.id ?? null}
            composing={composing}
            onComposing={setComposing}
            onJump={jump}
            onSubmitted={() => setSubmissions((previous) => previous + 1)}
          />
        )}

        {/* Eingeklappt, bis jemand sie aufklappt — außer unmittelbar nach der
            Abgabe: Dann ist sie das, was der Anwender gerade freigeschaltet hat. */}
        <ComparisonPanel
          key={submissions}
          client={client}
          revisionId={revisionId}
          role={role}
          defaultOpen={submissions > 0}
        />

        {handover.shown === null ? null : (
          <HandoffDialog
            shown={handover.shown}
            client={client}
            onCopy={handover.copyAgain}
            onClose={handover.close}
          />
        )}
      </ListPage>
    </>
  );
}

/**
 * Der Kopf der Ansicht: **eine Zeile, und darin ausschließlich Auskunft** — links
 * die Revision als Titel, rechts ihre SVN-Kennung, ihr Zeitpunkt und ihr
 * Bearbeitungsstand.
 *
 * Zwei Zeilen standen hier, und die untere war die Navigation: zurück in die
 * Liste, eine Revision vor, eine zurück, dazu die Übergabe. **Sie ist mit #41 in
 * die Werkzeugleiste gezogen.** Der Grund ist die Leitentscheidung dieses
 * Tickets: Was auf den Screen als Ganzen wirkt, steht in der Leiste; was der
 * Screen *ist*, steht im Inhalt. Vier Knöpfe verstreut im Kopf einer Fläche waren
 * genau das, was die Leiste ersetzt — und sie scrollten weg.
 *
 * **Der Commit-Kommentar steht hier nicht mehr.** Er stand einzeilig geklemmt
 * zwischen dem Zurück-Schalter und der Kennung, vollständig nur im `title` — und
 * das ist keine Lesefläche: nicht markierbar, nicht kopierbar, ohne Maus nicht
 * erreichbar. Er hat jetzt seinen eigenen Abschnitt in der Fläche. Zwei
 * Darstellungen derselben Zeichenkette wenige Pixel übereinander wären eine zu
 * viel, und eine Ellipse, hinter der nichts mehr verborgen ist, behauptete etwas
 * Falsches über den Inhalt.
 */
function RevisionHeader({ detail }: { detail: RevisionDetail | null }): ReactElement {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--sp-5)",
        padding: "var(--sp-6) var(--sp-5)",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-4)",
          fontSize: "var(--fs-13)",
          fontWeight: "var(--fw-semibold)",
          color: "var(--fg-1)",
        }}
      >
        {/* 22 px ausgeschrieben: Die Vorgabe des Bausteins sind 26, und die
            rissen diese Zeile mit ihrem 13-px-Titel auf. Die Glyphe rechnet er
            daraus, der Blauton kommt aus seinem Token (#151). */}
        <IconTile icon="layers" size={22} />
        {detail === null ? "Revision" : `Revision ${formatNumber(detail.revision.number)}`}
      </span>

      {detail === null ? null : (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--sp-5)",
            minWidth: 0,
          }}
        >
          <span style={{ flex: "none", fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
            {detail.revision.svnIdentity}
          </span>
          <span style={{ flex: "none", fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
            {formatTimestamp(detail.revision.committedAt)}
          </span>

          <StateBadge state={detail.revision.state} />
        </span>
      )}
    </header>
  );
}

/**
 * Der **Commit-Kommentar**, als eigener Abschnitt zwischen dem Kopf und den
 * Dateien.
 *
 * Er gehört zum Review: Er sagt, was beabsichtigt war, der Diff sagt, was
 * geschehen ist, und die beiden nebeneinander zu halten ist die Arbeit, die hier
 * getan wird. Der Widerspruch zwischen Nachricht und Änderung ist ausdrücklich
 * ein Befund, den ein Mensch macht — der Text muss also vor ihm stehen und nicht
 * unter einem Mauszeiger.
 *
 * Vier Festlegungen:
 *
 * - **Vollständig, mit `pre-wrap`.** SVN-Kommentare tragen Struktur —
 *   Aufzählungen, Ticketnummern —, und sie zu Fließtext zu glätten verliert sie.
 * - **Keine Klemme, keine Ellipse, kein `title`.** Markieren und Kopieren muss
 *   gehen: Wer den Widerspruch meldet, zitiert die Nachricht.
 * - **Eine lesbare Zeilenlänge** statt der ganzen Fensterbreite — auf einem
 *   breiten Schirm ist ein Absatz über 2000 Pixel nicht zu lesen. `anywhere`
 *   dazu, damit ein eingeklebter Pfad die Fläche nicht doch breiter macht.
 * - **Kein Einklappen** und **kein Sonderfall für die Blindbefundung.** Die
 *   Blindabsicht hält fremde Befunde und das Wärmebild zurück (ADR-0013); der
 *   Kommentar ist keins von beidem, sondern Teil der Änderung, über die geurteilt
 *   wird — und er steht ohnehin in jeder Zeile des Revisionsstroms, aus dem diese
 *   Ansicht geöffnet wird.
 *
 * Dazu die fünfte: **Ein erkannter Ticketverweis wird zum Link** (#101). Kein
 * zusätzlicher Knopf und keine zweite Zeile — der Verweis steht ja schon im
 * Text, er wird nur benutzbar. Erkannt hat ihn der Server; hier wird nichts
 * gesucht und nichts ausgelegt.
 */
function CommitMessage({
  message,
  parts,
}: {
  message: string;
  parts: MessagePart[];
}): ReactElement {
  // `revisions.message` ist `NOT NULL`, aber ohne `<msg>` legt der Poller den
  // leeren String ab. Der Abschnitt bleibt dann stehen und sagt es: Ein
  // fehlender Kommentar ist beim Review eine Auskunft und kein Nichts, und ein
  // weggelassener Abschnitt wäre von einem übersehenen nicht zu unterscheiden.
  const empty = message.trim() === "";

  return (
    <ListSection title="Commit-Kommentar" glyph="message-square">
      <span
        style={{
          padding: "0 var(--sp-5) var(--sp-6)",
          maxWidth: "84ch",
          whiteSpace: empty ? undefined : "pre-wrap",
          overflowWrap: "anywhere",
          fontSize: "var(--fs-12)",
          lineHeight: "var(--lh-normal)",
          color: empty ? "var(--fg-6)" : "var(--text-body)",
        }}
      >
        {empty
          ? "Diese Revision wurde ohne Kommentar eingecheckt."
          : parts.map((part, index) => <MessageSegment key={index} part={part} />)}
      </span>
    </ListSection>
  );
}

/**
 * Ein Abschnitt des Kommentars: Text, oder ein Ticketverweis als Link.
 *
 * **Der übrige Text bleibt unverändert** und wird nicht als Markdown ausgelegt —
 * ein Commit-Kommentar ist keine Auszeichnungssprache, und ein Unterstrich darin
 * ist ein Unterstrich.
 *
 * **Unterstrichen, in der Linkfarbe des Systems.** Die Farbe und die
 * Hervorhebung unter dem Zeiger stellt `tokens/base.css` für jedes `a`; die
 * Unterstreichung kommt hier dazu, weil dieser Link mitten im Fließtext steht
 * und Farbe allein ihn dort nicht von seinem Nachbarn trennt. Geführt wird die
 * Hervorhebung vom Browser und nicht von einem Kennzeichen in React — ein
 * solches bliebe stehen, wenn das `mouseleave` ausfällt.
 *
 * **Neuer Tab.** Wer aus einer Revision ins Ticket springt, ist mit der Revision
 * nicht fertig; sie unter ihm wegzuziehen nähme ihm den Stand, an dem er
 * gerade arbeitet — samt geöffneter Datei und Markierung.
 */
function MessageSegment({ part }: { part: MessagePart }): ReactElement {
  if (part.address === null) {
    return <>{part.text}</>;
  }

  return (
    <a
      href={part.address}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "underline" }}
    >
      {part.text}
    </a>
  );
}

/**
 * Der Schrittschalter: eine Revision zurück, eine vor.
 *
 * **Die Nummer steht am Schalter**, im Titel und als Text daneben. Zwei blanke
 * Pfeile sagten nur, dass es weitergeht — nicht, wohin; und zwischen r4.688 und
 * r4.710 liegt für den, der eine Reihe durchgeht, der ganze Unterschied.
 *
 * Am Rand des Bestands ist der Schalter grau und sagt im Titel, warum. Ein
 * Schalter, der dort verschwände, ließe die Zeile bei jeder Revision anders
 * aussehen — und der Anwender suchte den Weg, den es zweimal gab und einmal
 * nicht.
 */
function RevisionStepper({
  detail,
  onOpenRevision,
}: {
  detail: RevisionDetail;
  onOpenRevision: (id: number) => void;
}): ReactElement {
  return (
    <span
      role="group"
      aria-label="Revisionen"
      style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)", flex: "none" }}
    >
      <StepButton
        neighbour={detail.previous}
        label="Vorherige Revision"
        empty={NO_PREVIOUS}
        glyph="chevron-left"
        onOpenRevision={onOpenRevision}
      />
      <StepButton
        neighbour={detail.next}
        label="Nächste Revision"
        empty={NO_NEXT}
        glyph="chevron-right"
        onOpenRevision={onOpenRevision}
      />
    </span>
  );
}

/**
 * Der Knopf „nächste Revision mit offenem Befund an meinem Code" (#185).
 *
 * **Er fragt den Befundvorrat und erfindet keine zweite Reihenfolge.** Welche
 * Revision die nächste ist, entscheidet damit dieselbe Ordnung, in der der
 * Anwender den Vorrat ohnehin liest — Schwere, dann Übereinstimmung, dann der
 * Anker. Eine eigene Ordnung hier hieße, dass „als Nächstes" an zwei Stellen
 * zweierlei bedeutet.
 *
 * **Gesprungen wird in die erste Stelle, deren Revision nicht die geöffnete
 * ist.** Was hier noch offen ist, hält ihn nicht auf: Er ist für „diese ist
 * durch", und der Sprung innerhalb der Revision hat seinen eigenen Weg (#187).
 *
 * **Gibt es keine, sagt er das und bewegt nichts.** Er verschwindet dabei nicht
 * und wird nicht stumm.
 */
function NextOwnCodeButton({
  client,
  revisionId,
  onOpenRevision,
  onSay,
}: {
  client: Pick<RevisionViewApi, "findingSites">;
  /** Die geöffnete Revision — sie ist das einzige, was übersprungen wird. */
  revisionId: number;
  onOpenRevision: (id: number) => void;
  /** Was zu sagen war: kein Ziel, oder der Wortlaut eines Fehlers. */
  onSay: (message: string | null) => void;
}): ReactElement {
  const [asking, setAsking] = useState(false);

  return (
    <Button
      variant="outline"
      size="sm"
      icon="chevrons-right"
      disabled={asking}
      title={NEXT_OWN_CODE_TITLE}
      onClick={() => {
        setAsking(true);
        // Erst leeren: Ein Satz vom letzten Druck stünde sonst neben einem Zug,
        // der gerade läuft, und behauptete dessen Ergebnis im Voraus.
        onSay(null);

        void nextOwnCodeRevision(client, revisionId).then(
          (found) => {
            setAsking(false);

            if (found === null) {
              onSay(NO_OWN_CODE_LEFT);

              return;
            }

            onOpenRevision(found);
          },
          (error: unknown) => {
            setAsking(false);
            onSay(describe(error));
          },
        );
      }}
    >
      {NEXT_OWN_CODE}
    </Button>
  );
}

/**
 * Die Revision, in die der Knopf führt — oder `null`, wenn es keine gibt.
 *
 * **Geblättert wird, bis eine andere Revision dasteht.** Stehen auf der ersten
 * Seite nur Stellen der geöffneten Revision, heißt das nicht, dass es keine
 * weitere gibt; abzubrechen wäre eine Auskunft, die der Vorrat nicht gegeben hat.
 *
 * Eine Stelle **ohne** Revision zählt nicht mit: Zu ihr führt kein Weg in die
 * Revisionsansicht, und ein Sprung dorthin wäre eine geratene Revision. Unter
 * „an meinem Code" kommt sie ohnehin nicht vor — ein Bestandsbefund trägt keinen
 * Committer —, und ein Sprungziel wird trotzdem nicht aus der Abwesenheit einer
 * Kennung gedeutet.
 */
async function nextOwnCodeRevision(
  client: Pick<RevisionViewApi, "findingSites">,
  current: number,
): Promise<number | null> {
  for (let page = 1; ; page += 1) {
    const answer = await client.findingSites({ ...OWN_OPEN_SITES, page });
    const found = answer.sites.find(
      (site) => site.revisionId !== null && site.revisionId !== current,
    );

    if (found !== undefined && found.revisionId !== null) {
      return found.revisionId;
    }

    if (page >= answer.pages) {
      return null;
    }
  }
}

function StepButton({
  neighbour,
  label,
  empty,
  glyph,
  onOpenRevision,
}: {
  neighbour: RevisionNeighbour | null;
  label: string;
  /** Was im Titel steht, wenn es in diese Richtung nicht weitergeht. */
  empty: string;
  glyph: string;
  onOpenRevision: (id: number) => void;
}): ReactElement {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={neighbour === null}
      title={neighbour === null ? empty : `${label} — r${formatNumber(neighbour.number)}`}
      icon={glyph}
      onClick={() => {
        if (neighbour !== null) {
          onOpenRevision(neighbour.id);
        }
      }}
    >
      {neighbour === null ? label : `r${formatNumber(neighbour.number)}`}
    </Button>
  );
}

/**
 * Was von einer Dateiänderung nötig ist, um einen Anker zu setzen — gefügt aus
 * der Dateiliste und der Revision, ohne dass der Diff geladen sein müsste.
 *
 * Genau deshalb steht es hier: Der Befundverlauf springt auch in eine Datei, die
 * noch niemand geöffnet hat.
 */
function anchoredFile(revision: Revision, change: FileChangeSummary): AnchoredFile {
  return {
    projectId: revision.projectId,
    path: change.path,
    revisionNumber: revision.number,
  };
}

/**
 * Die Dateiliste: der Umfang der Revision, bevor jemand liest. Je Eintrag Pfad
 * und Art der Änderung, dazu der Grund, wenn die Änderung übersprungen wurde —
 * eine übersprungene Datei gehört zum Bild und ist nur nicht begutachtet.
 *
 * **Zwei Gestalten des Übersprungs, sichtbar getrennt** (ADR-0032): Die Marke
 * des Pollers steht ohne Urheber da, denn sie gilt für alle. Der Übersprung
 * eines Agenten steht mit seinem Namen — „Von „Gemma4" übersprungen: …" —, denn
 * er gilt nur für ihn, und wer die Datei ansieht, muss lesen können, wer sie
 * nicht gesehen hat.
 */
function FileList({
  changes,
  chosenId,
  onChoose,
}: {
  changes: FileChangeSummary[];
  chosenId: number;
  onChoose: (id: number) => void;
}): ReactElement {
  return (
    <div
      role="group"
      aria-label="Dateiliste"
      className="erp-scroll"
      style={{
        width: "var(--sidebar-width)",
        flex: "none",
        maxHeight: 560,
        overflowY: "auto",
        borderRight: "1px solid var(--line-f)",
      }}
    >
      {changes.map((change) => (
        <button
          key={change.id}
          type="button"
          title={change.path}
          aria-current={change.id === chosenId ? "true" : undefined}
          onClick={() => onChoose(change.id)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--sp-3)",
            width: "100%",
            padding: "var(--sp-5) var(--sp-6)",
            border: "none",
            borderBottom: "1px solid var(--line-f)",
            background:
              change.id === chosenId ? "var(--surface-row-hover)" : "transparent",
            color: "var(--text-body)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--fs-12)",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {/* Der Pfad steht vollständig da und bricht um: Abgeschnitten
              unterschieden sich zwei Dateien desselben Verzeichnisses nicht. */}
          <span
            style={{
              maxWidth: "100%",
              overflowWrap: "anywhere",
              color: change.id === chosenId ? "var(--fg-1)" : "var(--text-body)",
            }}
          >
            {change.path}
          </span>

          <StatusPill tone={ACTION_TONES[change.changeAction]}>
            {ACTION_LABELS[change.changeAction]}
          </StatusPill>

          {change.skippedReason === null ? null : (
            <span
              style={{
                fontSize: "var(--fs-11)",
                color: "var(--fg-6)",
                textWrap: "pretty",
              }}
            >
              {change.skippedReason}
            </span>
          )}

          {change.agentSkips.map((skip) => (
            <span
              key={skip.agentId}
              style={{
                fontSize: "var(--fs-11)",
                color: "var(--fg-6)",
                textWrap: "pretty",
              }}
            >
              {`Von „${skip.agentName}" übersprungen: ${skip.reason}`}
            </span>
          ))}
        </button>
      ))}
    </div>
  );
}

/**
 * Die geöffnete Dateiänderung: Umschalter, Markierungszeile, Code.
 *
 * Der Umschalter liegt oben — wer den Diff liest, will ihn auch in der nächsten
 * Datei lesen —, und die Markierung ebenfalls: Sie überlebt den Sprung aus dem
 * Befundverlauf in eine andere Datei.
 */
function FilePanel({
  client,
  change,
  presentation,
  onPresentation,
  anchor,
  marking,
  arrived,
  refusal,
  onPick,
  onCompose,
}: {
  client: RevisionViewApi;
  change: FileChangeSummary;
  presentation: Presentation;
  onPresentation: (presentation: Presentation) => void;
  anchor: FindingAnchor | null;
  marking: Marking | null;
  /**
   * Ob die Markierung aus einem **Sprung** stammt — aus dem Befundvorrat oder
   * aus dem Befundverlauf. Dann holt der Code sie sich ins Bild.
   */
  arrived: boolean;
  /** Warum der letzte Klick keine Markierung ergeben hat, oder `null`. */
  refusal: string | null;
  onPick: Picking;
  /** Aus der Markierung einen Befund machen — das Formular gehört dem Befundverlauf. */
  onCompose: () => void;
}): ReactElement {
  const { state } = useLoaded(() => client.fileChange(change.id));

  const detail = state.kind === "loaded" ? state.value : null;
  const code = useRef<HTMLDivElement | null>(null);

  // Wie oft zwischen Diff und vollständiger Datei umgeschaltet wurde — nicht
  // *ob* gerade umgeschaltet ist, sondern *dass* umgeschaltet wurde, so wie
  // `jumps` in der Ansicht: Zweimal hin und her holt die Markierung zweimal
  // heran, auch wenn `presentation` am Ende wieder denselben Wert trägt.
  const [switches, setSwitches] = useState(0);

  const selectPresentation = (next: Presentation): void => {
    if (next !== presentation) {
      setSwitches((previous) => previous + 1);
    }

    onPresentation(next);
  };

  // Die markierte Zeile ins Bild holen — gefunden über `aria-pressed`, das die
  // Codezeile ohnehin trägt. Ein zweiter Weg durch vier Ebenen von Eigenschaften
  // brächte dieselbe Zeile und vier weitere Stellen, an denen sie verloren geht.
  //
  // **Nur in diesem Kasten.** Wohin die Seite springt, entscheidet die Ansicht:
  // Sie holt die ganze Fläche an ihren Anfang. Zöge die Zeile die Seite mit,
  // landete ein Sprung in der Mitte der Fläche — dort steht die Zeile —, und ihr
  // Kopf mit Umschalter und Markierungszeile stünde oberhalb des Bildrands.
  useEffect(() => {
    if (!arrived || detail === null) {
      return;
    }

    bringWithin(code.current, code.current?.querySelector('[aria-pressed="true"]'));
  }, [arrived, detail, marking]);

  // Die Umschaltung selbst holt die Markierung erneut ins Bild — unabhängig
  // davon, woher sie stammt. `arrived` sperrt hier nicht: Ein Klick auf eine
  // Zeile setzt `arrived` auf `false`, weil er selbst nicht scrollen soll, aber
  // er hat mit dem Umschalten nichts zu tun. Ist die Zeile in der
  // Zieldarstellung nicht vorhanden, findet die Abfrage nichts, und
  // `bringWithin` lässt die Fläche stehen, wie sie ist.
  useEffect(() => {
    if (switches === 0 || detail === null) {
      return;
    }

    bringWithin(code.current, code.current?.querySelector('[aria-pressed="true"]'));
  }, [switches, detail]);

  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-4)",
          padding: "var(--sp-4) var(--sp-6)",
          borderBottom: "1px solid var(--line-f)",
        }}
      >
        {/* Seit #41 die Registerleiste des Baukastens: **eine** Leiste mit einer
            Liste von Reitern statt einer Reihe von Aufrufen je Reiter. Gewählt
            wird über die Beschriftung — eine Kennung neben dem Text führt der
            Baustein nicht. */}
        <TabStrip
          tabs={PRESENTATION_TABS}
          value={PRESENTATION_LABELS[presentation]}
          onSelect={(label) => selectPresentation(presentationOf(label))}
          style={{ flex: "none" }}
        />

        <span style={{ flex: 1 }} />

        <span
          title={change.path}
          style={{
            maxWidth: 420,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "var(--fs-11)",
            color: "var(--fg-6)",
          }}
        >
          {change.path}
        </span>
      </div>

      <MarkingBar anchor={anchor} refusal={refusal} onCompose={onCompose} />

      <div
        ref={code}
        className="erp-scroll"
        style={{ flex: 1, maxHeight: 520, overflow: "auto", padding: "var(--sp-4) 0" }}
      >
        {state.kind === "loading" ? (
          <div style={{ padding: "0 var(--sp-6)" }}>
            <Notice text="Wird geladen ..." glyph="timer" />
          </div>
        ) : null}
        {state.kind === "failed" ? (
          <div style={{ padding: "0 var(--sp-6)" }}>
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          </div>
        ) : null}

        {detail === null || detail.sourceEncoding === null ? null : (
          <SourceEncodingNote encoding={detail.sourceEncoding} />
        )}

        {detail === null ? null : (
          <Code
            detail={detail}
            presentation={presentation}
            marking={marking}
            // Die erlaubten Zeilen kommen aus dem Diff, den diese Ansicht
            // ohnehin geliefert bekommt — kein Feld am API-Vertrag kommt dafür
            // hinzu. Gerechnet wird beim Klick und nicht bei jedem Zeichnen.
            onPick={(side, line, extend) => onPick(side, line, extend, findableLines(detail))}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Der Vermerk an einer Dateiänderung, deren Text nicht als UTF-8 kam: Er wurde
 * beim Ablegen aus der eingestellten Kodierung gewandelt (ADR-0023).
 *
 * Er steht über dem Code und damit in beiden Darstellungen — Diff und Inhalt
 * kommen aus derselben Datei und tragen dieselben Bytes. Wer einen Befund an
 * einem Sonderzeichen liest, muss wissen, dass der Text durch eine Wandlung
 * gegangen ist; ohne diesen Satz wäre die Wandlung ein stilles Ersetzen.
 */
function SourceEncodingNote({ encoding }: { encoding: string }): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-4)",
        padding: "0 var(--sp-6) var(--sp-4)",
        fontSize: "var(--fs-11)",
        color: "var(--fg-6)",
      }}
    >
      <Icon name="triangle-alert" size={15} />
      <span>{`Aus ${encoding} nach UTF-8 gewandelt — Sonderzeichen können abweichen.`}</span>
    </div>
  );
}

/**
 * Was die Markierung ergibt — Datei, Revision, Zeilenbereich —, in genau der
 * Form, in der ein Befund es trägt.
 *
 * Und wenn nichts daraus wird: **warum nicht**. Der Satz steht neben dem Anker
 * und nicht als Meldung irgendwo — wer eine Zeile anklickt und nichts geschehen
 * sieht, sucht den Fehler bei sich (ADR-0019).
 */
function MarkingBar({
  anchor,
  refusal,
  onCompose,
}: {
  anchor: FindingAnchor | null;
  refusal: string | null;
  onCompose: () => void;
}): ReactElement {
  return (
    <section
      aria-label="Markierung"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-5)",
        padding: "var(--sp-4) var(--sp-6)",
        borderBottom: "1px solid var(--line-f)",
        background: "var(--bg-sunken)",
        fontSize: "var(--fs-11)",
      }}
    >
      {anchor === null ? (
        <span style={{ color: "var(--fg-6)" }}>{NOTHING_MARKED}</span>
      ) : (
        <span style={{ color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}>
          {describeAnchor(anchor)}
        </span>
      )}

      {refusal === null ? null : (
        <span style={{ color: "var(--ac-amber)", textWrap: "pretty" }}>{refusal}</span>
      )}

      <span style={{ flex: 1 }} />

      {/* Der Knopf steht hier und nicht nur oben im Befundverlauf: Markiert wird
          im Code, und wer gerade markiert hat, soll nicht scrollen müssen, um den
          Befund dazu anzulegen. */}
      <Button
        variant="outline"
        size="sm"
        disabled={anchor === null}
        icon="plus"
        onClick={onCompose}
      >
        Befund anlegen
      </Button>
    </section>
  );
}

/** Der Code selbst: entweder die Hunks des Diffs oder die vollständige Datei. */
function Code({
  detail,
  presentation,
  marking,
  onPick,
}: {
  detail: FileChangeDetail;
  presentation: Presentation;
  marking: Marking | null;
  onPick: (side: LineSide, line: number, extend: boolean) => void;
}): ReactElement {
  // **Nur die Marke des Pollers verriegelt** (ADR-0032): Dort sind Diff und
  // Inhalt leer, es gäbe nichts zu zeigen. Der Übersprung eines Agenten leert
  // nichts — ein Mensch darf die Datei ansehen, auch wenn ein Agent sie nicht
  // ansehen konnte, und sein Grund steht in der Dateiliste daneben statt
  // anstelle der Datei.
  if (detail.skippedReason !== null) {
    return (
      <div style={{ padding: "0 var(--sp-6)" }}>
        <Notice text={`Übersprungen: ${detail.skippedReason}`} glyph="triangle-alert" />
      </div>
    );
  }

  // Die Sprache hängt an der Datei und nicht an der Darstellung: Diff, vollständige
  // Datei und Ausschnitt tönen dieselbe Zeile gleich (ADR-0026).
  const language = languageOf(detail.path);

  if (presentation === "file") {
    return <WholeFile detail={detail} language={language} marking={marking} onPick={onPick} />;
  }

  if (detail.hunks.length === 0) {
    return (
      <div style={{ padding: "0 var(--sp-6)" }}>
        <Notice text="Diese Revision hat den Text dieser Datei nicht verändert." />
      </div>
    );
  }

  // Alle Abschnitte in **einer** Fläche: Zwei Flächen wären zwei Breiten, und die
  // Nummernspalten des zweiten Abschnitts stünden anders als die des ersten.
  return (
    <CodeLines>
      {detail.hunks.map((hunk, index) => (
        <Hunk key={index} hunk={hunk} language={language} marking={marking} onPick={onPick} />
      ))}
    </CodeLines>
  );
}

function Hunk({
  hunk,
  language,
  marking,
  onPick,
}: {
  hunk: DiffHunk;
  language: Language;
  marking: Marking | null;
  onPick: (side: LineSide, line: number, extend: boolean) => void;
}): ReactElement {
  // Zwei Ströme je Abschnitt, einer je Seite — gerechnet einmal und gemerkt.
  const tinted = useMemo(() => tintDiff(hunk.lines, language), [hunk, language]);

  return (
    <div>
      <div
        style={{
          padding: "var(--sp-3) var(--sp-6)",
          color: "var(--fg-6)",
          fontSize: "var(--fs-11)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {`@@ -${hunk.oldStart},${hunk.oldCount} +${hunk.newStart},${hunk.newCount} @@ ${hunk.heading}`.trim()}
      </div>

      {hunk.lines.map((line, index) => (
        <DiffCodeLine
          key={index}
          line={line}
          tokens={tinted[index] ?? []}
          marking={marking}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

function DiffCodeLine({
  line,
  tokens,
  marking,
  onPick,
}: {
  line: DiffLine;
  tokens: Token[];
  marking: Marking | null;
  onPick: (side: LineSide, line: number, extend: boolean) => void;
}): ReactElement {
  const side = sideOfDiffLine(line.kind);
  const number = side === "old" ? line.oldLine : line.newLine;

  return (
    <CodeLine
      label={labelOfDiffLine(line, number)}
      gutter={[line.oldLine, line.newLine]}
      sign={line.kind === "added" ? "+" : line.kind === "removed" ? "-" : " "}
      tokens={tokens}
      tint={line.kind}
      marked={number !== null && covers(marking, side, number)}
      onPick={(extend) => {
        if (number !== null) {
          onPick(side, number, extend);
        }
      }}
    />
  );
}

/**
 * Die vollständige Datei — der Grund, warum die Markierung nicht am Diff hängt:
 * Der Anker ist Datei @ Revision und keine Position in einem Diff (ADR-0006),
 * und derselbe Anker gilt in beiden Darstellungen.
 *
 * Gezeigt wird jede Zeile, angenommen wird nur die geänderte: Der Zusammenhang
 * ist zum Lesen da, befundet wird, was diese Revision geschrieben hat.
 *
 * Bei einer gelöschten Datei ist der abgelegte Inhalt der Stand aus der
 * Vorgängerrevision, und die Ansicht sagt das sichtbar dazu. Markierbar ist er
 * nicht — es gibt niemanden, der den Code beheben könnte (ADR-0019).
 */
function WholeFile({
  detail,
  language,
  marking,
  onPick,
}: {
  detail: FileChangeDetail;
  language: Language;
  marking: Marking | null;
  onPick: (side: LineSide, line: number, extend: boolean) => void;
}): ReactElement {
  // Ein Strom über alle Zeilen, gerechnet einmal je Dateiänderung.
  const tinted = useMemo(
    () => highlight(contentLines(detail.content ?? ""), language),
    [detail.content, language],
  );

  if (detail.content === null) {
    return (
      <div style={{ padding: "0 var(--sp-6)" }}>
        <Notice text="Für diese Datei ist kein Inhalt abgelegt." />
      </div>
    );
  }

  const side = sideOfWholeFile();
  const lines = contentLines(detail.content);

  return (
    <>
      {detail.contentRevision === detail.revisionNumber ? null : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--sp-4)",
            padding: "0 var(--sp-6) var(--sp-4)",
            fontSize: "var(--fs-11)",
            color: "var(--fg-6)",
          }}
        >
          <Icon name="triangle-alert" size={15} />
          <span>
            {`Stand aus r${formatNumber(detail.contentRevision)} — in r${formatNumber(detail.revisionNumber)} gibt es diese Datei nicht mehr.`}
          </span>
        </div>
      )}

      <CodeLines>
        {lines.map((text, index) => (
          <CodeLine
            key={index}
            label={`Zeile ${index + 1}`}
            gutter={[null, index + 1]}
            sign=" "
            tokens={tinted[index] ?? []}
            tint="context"
            marked={covers(marking, side, index + 1)}
            onPick={(extend) => onPick(side, index + 1, extend)}
          />
        ))}
      </CodeLines>
    </>
  );
}
